/**
 * @module @repo/global-login-sdk/dialog
 * Wiederverwendbare, a11y-konforme Dialog-Primitive — Tailwind-only und ohne
 * UI-Kit-Abhängigkeit, damit sie in jede Host-App passt und deren Theme-Tokens
 * (`bg-background`, `text-foreground`, `text-muted-foreground`, `border`, `--ring`)
 * nutzt. Enthält Fokus-Trap, Esc-/Overlay-Schließen, Body-Scroll-Lock (mit
 * Scrollbar-Shift-Kompensation) und eine `prefers-reduced-motion`-sichere
 * Einblendung ohne Layout-Shift.
 */
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from './cn';

/** CSS-Selektor für nativ fokussierbare Elemente innerhalb des Panels. */
const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Selbst-enthaltene Einblend-Animation. Bewusst als `<style>` statt via
 * `tailwindcss-animate`, damit die Primitive nicht von Host-Plugins abhängt.
 * `prefers-reduced-motion` schaltet die Animation vollständig ab; der Endzustand
 * ist `transform: none`, sodass kein Transform zurückbleibt und nichts springt.
 */
const DIALOG_ANIMATION_CSS = `
@keyframes gli-dialog-overlay-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes gli-dialog-panel-in {
  from { opacity: 0; transform: translateY(6px) scale(0.98) }
  to { opacity: 1; transform: none }
}
[data-gli-dialog-overlay] { animation: gli-dialog-overlay-in 140ms ease-out }
[data-gli-dialog-panel] { animation: gli-dialog-panel-in 180ms cubic-bezier(0.16, 1, 0.3, 1) }
@media (prefers-reduced-motion: reduce) {
  [data-gli-dialog-overlay], [data-gli-dialog-panel] { animation: none }
}`;

/** Props der {@link Dialog}-Primitive. */
export interface DialogProps {
  /** Steuert die Sichtbarkeit; bei `false` wird nichts gerendert. */
  open: boolean;
  /** Wird bei Esc, Overlay-Klick oder Schließen-Button aufgerufen. */
  onClose: () => void;
  /** Zugänglicher Titel (verknüpft via `aria-labelledby`). */
  title: string;
  /** Optionale Beschreibung (verknüpft via `aria-describedby`). */
  description?: string;
  /** Inhalt des Dialog-Körpers. */
  children: ReactNode;
}

/**
 * Modaler Dialog mit vollständigem a11y-Verhalten: `role="dialog"` +
 * `aria-modal`, generierte `aria-labelledby`/`aria-describedby`-IDs, Fokus-Trap
 * (erstes Element fokussieren, Tab/Shift+Tab zyklisch, Fokus beim Schließen
 * zurückgeben), Esc- und Overlay-Klick zum Schließen, Body-Scroll-Lock solange
 * offen sowie sichtbarer Fokusring.
 *
 * @param props Siehe {@link DialogProps}.
 * @returns Das gerenderte Overlay + Panel oder `null`, wenn geschlossen.
 */
export function Dialog(props: DialogProps): ReactNode {
  const { open, onClose, title, description, children } = props;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    const panel = panelRef.current;
    if (panel) {
      const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? panel).focus();
    }
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  // Body-Scroll-Lock, solange der Dialog offen ist: sperrt das Scrollen des
  // Hintergrunds und kompensiert die verschwindende Scrollbar (kein Layout-Shift).
  // Der vorherige Inline-Wert wird gemerkt und exakt wiederhergestellt (nicht hart
  // auf '' gesetzt), damit App-eigene Inline-Styles erhalten bleiben.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const currentPadding = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  if (!open) return null;

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    // noUncheckedIndexedAccess: length > 0 ist oben bereits garantiert, aber der
    // Compiler engt Array-Index-Zugriffe nicht darueber ein — expliziter Guard
    // (zur Laufzeit ein No-op fuer length > 0), damit first/last auf HTMLElement einengen.
    if (!first || !last) return;
    const active = document.activeElement;
    if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      data-gli-dialog-overlay
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
    >
      <style dangerouslySetInnerHTML={{ __html: DIALOG_ANIMATION_CSS }} />
      <div
        data-gli-dialog-panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-xl border bg-background text-foreground shadow-lg focus:outline-none"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b px-6 py-4">
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="text-base font-semibold leading-tight">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="-mt-1 -mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

/** Props von {@link DialogButton} — erweitert die nativen `<button>`-Attribute. */
export interface DialogButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Optische Variante. Default `primary` (hoher Kontrast), sonst `secondary`. */
  variant?: 'primary' | 'secondary';
}

/**
 * Konsistenter Dialog-Button (primär/sekundär). Nutzt ausschließlich die
 * garantierten Theme-Tokens (`foreground`/`background`) für hohen Kontrast in
 * hellem wie dunklem Theme; default `type="button"`, mit sichtbarem Fokusring.
 *
 * @param props Siehe {@link DialogButtonProps} plus native `<button>`-Attribute.
 * @returns Der gerenderte Button.
 */
export function DialogButton(props: DialogButtonProps): ReactNode {
  const { variant = 'primary', className, type, ...rest } = props;
  return (
    <button
      type={type ?? 'button'}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
        'focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-60',
        variant === 'primary'
          ? 'bg-foreground text-background hover:bg-foreground/90'
          : 'border bg-background text-foreground hover:bg-foreground/5',
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Schlichter, `prefers-reduced-motion`-bewusster Lade-Spinner (rotiert nur bei
 * `motion-safe`). Wird intern in den SDK-Dialogen für Pending-Zustände genutzt.
 *
 * @param props.className Zusätzliche Utility-Klassen (z. B. Größe/Farbe).
 * @returns Das gerenderte SVG.
 */
export function Spinner(props: { className?: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn('size-4 motion-safe:animate-spin', props.className)}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M12 3a9 9 0 0 1 9 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
